import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { DateFormats } from 'helpers/enums/DateTypes';
import { getFormatedDateTime } from 'helpers/utils/DateUtils';
import { getFileNameFromContentDispositionOrDefault } from 'helpers/utils/HTMLUtils';
import { ExtraQueryParams } from 'helpers/utils/reactQuery';
import { getAssembleiasAssessorBffEndpoint } from 'library/Apis';
import { Mutations, Queries } from 'library/queries';
import {
  ReportInfoPage,
  ReportQueryParams,
  ReportType,
  ReportTypes,
} from 'models/relatory.model';
import ApiService from './ApiService';

export const useGetPageReportInfo = (
  id: string,
  extra?: ExtraQueryParams<ReportInfoPage>
) => {
  const url = `${getAssembleiasAssessorBffEndpoint()}event/?eventId=${id}`;

  const queryFn = async () => {
    const req = await ApiService.get<ReportInfoPage>(url);
    return req.data;
  };

  return useQuery({
    queryKey: [Queries.GetReportInfo, id],
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 1,
    retryDelay: 1000 * 2, // 2 seconds
    ...(extra ?? {}),
    queryFn,
  });
};

export const useGetReports = (
  id: string,
  extra?: ExtraQueryParams<ReportTypes[]>
) => {
  const url = `${getAssembleiasAssessorBffEndpoint()}event/reports?eventId=${id}`;

  const queryFn = async () => {
    const req = await ApiService.get<ReportTypes[]>(url);
    return req.data;
  };

  return useQuery({
    queryKey: [Queries.GetReports, id],
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 1,
    retryDelay: 1000 * 2, // 2 seconds
    ...(extra ?? {}),
    queryFn,
  });
};

// Download reports

export const useReportDownload = ({
  url,
  id,
  fileNamePrefix,
  queryKey,
  isPdf,
  method = 'GET',
  extra,
}: ReportQueryParams): UseQueryResult<{
  response: AxiosResponse<ArrayBuffer>;
  fileName: string;
}> => {
  const fileDate = getFormatedDateTime(Date.now(), DateFormats.ISOFile);

  const queryFn = async () => {
    try {
      if (method === 'POST') {
        const response = await ApiService.post<ArrayBuffer>(url, {
          responseType: 'arraybuffer',
        });

        const fileName = getFileNameFromContentDispositionOrDefault(
          response.headers,
          `${fileNamePrefix}_${fileDate}_Evento_${id}.${isPdf ? 'pdf' : 'xlsx'}`
        );

        return { data: response.data, fileName: fileName };
      } else {
        const response = await ApiService.get<ArrayBuffer>(url, {
          responseType: 'arraybuffer',
        });

        const fileName = getFileNameFromContentDispositionOrDefault(
          response.headers,
          `${fileNamePrefix}_${fileDate}_Evento_${id}.${isPdf ? 'pdf' : 'xlsx'}`
        );

        return { data: response.data, fileName: fileName };
      }
    } catch (error) {
      const errorData = (error as AxiosError).response?.data;
      if (errorData instanceof ArrayBuffer) {
        const decoded = new TextDecoder().decode(errorData);
        const errObj = JSON.parse(decoded);
        throw errObj;
      } else {
        throw error;
      }
    }
  };

  return useQuery({
    queryKey: [queryKey, id],
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    retryDelay: 1000 * 2, // 2 seconds
    queryFn,
    ...(extra ?? {}),
  });
};

export const useGenerateReport = () => {
  const url = `${getAssembleiasAssessorBffEndpoint()}event/reports/generate`;

  const mutationFn = async (data: {
    eventId: number;
    reportType: ReportType;
  }) => {
    try {
      return await ApiService.post(url, data);
    } catch (error) {
      throw error;
    }
  };

  return useMutation({
    mutationKey: [Mutations.PostReportGenerate],
    mutationFn,
    throwOnError: false,
    retry: 1,
    retryDelay: 1000 * 2, // 2 seconds
    gcTime: Infinity,
  });
};
